/** Persyaratan templates (staf) + submissions (mahasiswa). */

import { createDoc, updateDoc, fetchQuery } from '../service';
import { COL, q, col, where } from '../../collections';
import { utcNowIso } from '../../format';
import type {
  PersyaratanAnswer,
  PersyaratanDoc,
  PersyaratanItem,
  PersyaratanScope,
  PersyaratanStage,
  PersyaratanSubmissionDoc,
} from '../../types';

function newItemId(): string {
  return `itm_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Create or replace the persyaratan template for a stage + scope. */
export async function savePersyaratanTemplate(input: {
  id?: string;
  stage: PersyaratanStage;
  scope: PersyaratanScope;
  jurusanId?: string;
  prodiId?: string;
  fakultas?: string;
  title: string;
  description?: string;
  items: PersyaratanItem[];
}) {
  const items = input.items.map((it) => ({ ...it, id: it.id || newItemId() }));
  const scopeFields = {
    scope: input.scope,
    jurusanId: input.scope === 'jurusan' ? input.jurusanId : undefined,
    prodiId: input.scope === 'prodi' ? input.prodiId : undefined,
    fakultas: input.scope === 'fakultas' ? input.fakultas : undefined,
  };
  if (input.id) {
    await updateDoc<PersyaratanDoc>(COL.persyaratan, input.id, {
      stage: input.stage,
      ...scopeFields,
      title: input.title,
      description: input.description,
      items,
    });
    return { id: input.id };
  }
  return createDoc<PersyaratanDoc>(COL.persyaratan, {
    stage: input.stage,
    ...scopeFields,
    title: input.title,
    description: input.description,
    items,
    status: 'active',
  });
}

export async function setPersyaratanTemplateStatus(id: string, status: PersyaratanDoc['status']) {
  return updateDoc<PersyaratanDoc>(COL.persyaratan, id, { status });
}

async function upsertPersyaratanSubmission(input: {
  skripsiId: string;
  stage: PersyaratanStage;
  answers: PersyaratanAnswer[];
  status: 'draft' | 'submitted';
  actorId: string;
  actorName?: string;
}) {
  const qref = q(
    col(COL.persyaratanSubmission),
    where('skripsiId', '==', input.skripsiId),
    where('stage', '==', input.stage),
  );
  const existing = await fetchQuery<PersyaratanSubmissionDoc>(qref);
  const now = utcNowIso();
  if (existing[0]) {
    const patch: Partial<PersyaratanSubmissionDoc> = { answers: input.answers };
    if (input.status === 'submitted') {
      patch.status = 'submitted';
      patch.submittedAt = now;
      patch.submittedBy = input.actorId;
    }
    await updateDoc<PersyaratanSubmissionDoc>(COL.persyaratanSubmission, existing[0].id, patch);
    return existing[0];
  }
  return createDoc<PersyaratanSubmissionDoc>(COL.persyaratanSubmission, {
    skripsiId: input.skripsiId,
    stage: input.stage,
    answers: input.answers,
    status: input.status,
    submittedAt: input.status === 'submitted' ? now : undefined,
    submittedBy: input.status === 'submitted' ? input.actorId : undefined,
  });
}

/** Save a persyaratan draft (does not finalize). */
export async function savePersyaratanDraft(input: {
  skripsiId: string;
  stage: PersyaratanStage;
  answers: PersyaratanAnswer[];
  actorId: string;
}) {
  return upsertPersyaratanSubmission({ ...input, status: 'draft' });
}

/** Finalize the persyaratan for a stage. */
export async function submitPersyaratan(input: {
  skripsiId: string;
  stage: PersyaratanStage;
  answers: PersyaratanAnswer[];
  actorId: string;
  actorName?: string;
}) {
  return upsertPersyaratanSubmission({ ...input, status: 'submitted' });
}
